import { create } from "zustand";
import type {
  BuildingDto,
  ConstructionJobDto,
  MeResponse,
  ProductionJobDto,
  ResearchJobDto,
  ServerBrowserCity,
} from "@stahlfront/shared";

export type Screen = "server-browser" | "auth" | "game";

/** Phase 2.D + 2.F.1 — references a job that the player wants to spend diamonds on. */
export type SpeedupTarget =
  | { readonly kind: "production"; readonly jobId: string }
  | { readonly kind: "construction"; readonly jobId: string }
  | { readonly kind: "research"; readonly jobId: string };

/** Phase 2.E — §6.4 Operations Dashboard rail category that is currently open. */
export type OpsCategory =
  | "search"
  | "tasks"
  | "resources"
  | "production"
  | "construction"
  | "research"
  | "marches"
  | "buffs";

export interface AppState {
  // Routing
  currentScreen: Screen;
  setCurrentScreen: (s: Screen) => void;
  selectedCity: ServerBrowserCity | null;
  setSelectedCity: (city: ServerBrowserCity | null) => void;

  // Auth
  me: MeResponse | null;
  setMe: (m: MeResponse | null) => void;

  // Base buildings (Phase 1.C)
  buildings: readonly BuildingDto[];
  setBuildings: (bs: readonly BuildingDto[]) => void;

  // §6.1 building info popup (Phase 1.D)
  selectedBuildingId: string | null;
  setSelectedBuildingId: (id: string | null) => void;

  // §6.2 production panel (Phase 1.E)
  productionPanelOpen: boolean;
  setProductionPanelOpen: (b: boolean) => void;
  productionPanelBuildingId: string | null;
  setProductionPanelBuildingId: (id: string | null) => void;
  productionJobs: readonly ProductionJobDto[];
  setProductionJobs: (jobs: readonly ProductionJobDto[]) => void;

  // §6.4.3 construction (Phase 2.C)
  constructionJobs: readonly ConstructionJobDto[];
  setConstructionJobs: (jobs: readonly ConstructionJobDto[]) => void;
  upgradeConfirmBuildingId: string | null;
  setUpgradeConfirmBuildingId: (id: string | null) => void;

  // §7.6 speed-up confirm modal (Phase 2.D)
  speedupConfirm: SpeedupTarget | null;
  setSpeedupConfirm: (t: SpeedupTarget | null) => void;

  // §6.4 Operations Dashboard rail (Phase 2.E)
  opsCategory: OpsCategory | null;
  setOpsCategory: (c: OpsCategory | null) => void;

  // §10 research (Phase 2.F.1)
  researchJobs: readonly ResearchJobDto[];
  setResearchJobs: (j: readonly ResearchJobDto[]) => void;
  completedNodeIds: readonly string[];
  setCompletedNodeIds: (ids: readonly string[]) => void;
  researchSlots: { used: number; max: number };
  setResearchSlots: (s: { used: number; max: number }) => void;
  researchPanelOpen: boolean;
  setResearchPanelOpen: (b: boolean) => void;

  // Game canvas
  zoom: number;
  setZoom: (z: number) => void;
  wsReady: boolean;
  setWsReady: (b: boolean) => void;
  diagnosticsOpen: boolean;
  toggleDiagnostics: () => void;
  fps: number;
  setFps: (n: number) => void;
}

export const useStore = create<AppState>((set) => ({
  currentScreen: "server-browser",
  setCurrentScreen: (s) => set({ currentScreen: s }),
  selectedCity: null,
  setSelectedCity: (city) => set({ selectedCity: city }),

  me: null,
  setMe: (m) => set({ me: m }),

  buildings: [],
  setBuildings: (bs) => set({ buildings: bs }),

  selectedBuildingId: null,
  setSelectedBuildingId: (id) => set({ selectedBuildingId: id }),

  productionPanelOpen: false,
  setProductionPanelOpen: (b) => set({ productionPanelOpen: b }),
  productionPanelBuildingId: null,
  setProductionPanelBuildingId: (id) => set({ productionPanelBuildingId: id }),
  productionJobs: [],
  setProductionJobs: (jobs) => set({ productionJobs: jobs }),

  constructionJobs: [],
  setConstructionJobs: (jobs) => set({ constructionJobs: jobs }),
  upgradeConfirmBuildingId: null,
  setUpgradeConfirmBuildingId: (id) => set({ upgradeConfirmBuildingId: id }),

  speedupConfirm: null,
  setSpeedupConfirm: (t) => set({ speedupConfirm: t }),

  opsCategory: null,
  setOpsCategory: (c) => set({ opsCategory: c }),

  researchJobs: [],
  setResearchJobs: (j) => set({ researchJobs: j }),
  completedNodeIds: [],
  setCompletedNodeIds: (ids) => set({ completedNodeIds: ids }),
  researchSlots: { used: 0, max: 0 },
  setResearchSlots: (s) => set({ researchSlots: s }),
  researchPanelOpen: false,
  setResearchPanelOpen: (b) => set({ researchPanelOpen: b }),

  zoom: 0,
  setZoom: (z) => set({ zoom: z }),
  wsReady: false,
  setWsReady: (b) => set({ wsReady: b }),
  diagnosticsOpen: true,
  toggleDiagnostics: () => set((s) => ({ diagnosticsOpen: !s.diagnosticsOpen })),
  fps: 0,
  setFps: (n) => set({ fps: n }),
}));

if (typeof window !== "undefined") {
  (window as unknown as { __sfStore?: typeof useStore }).__sfStore = useStore;
}
